import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  IManageSiteNavigationSlice,
  ISiteNavigationInstanceState,
  initialSiteNavigationInstanceState,
} from '../../Models/ManageSiteNavigation'
import { setLoader } from './loader.slice'
import mainApiService from '../../Services'
import { SITE_NAVIGATION_CRUD, WIDGET_MANAGEMENT_CRUD } from '../../Constants/ApiDefinations'

export interface SiteNavigationCRUDAPIDefinitionModal {
  url: string
  method: string
  data: any
}

const initialState: IManageSiteNavigationSlice = {
  navigationsById: {},
}

const ensureNavigation = (state: IManageSiteNavigationSlice, navigationId: string) => {
  if (!state.navigationsById) {
    state.navigationsById = {}
  }
  if (!state.navigationsById[navigationId]) {
    state.navigationsById[navigationId] = { ...initialSiteNavigationInstanceState }
  }
}

const siteNavigationSlice = createSlice({
  name: 'siteNavigation',
  initialState,
  reducers: {
    setDefinitionDetails: (state, action: PayloadAction<{ navigationId: string; data: any }>) => {
      const { navigationId, data } = action.payload
      ensureNavigation(state, navigationId)
      state.navigationsById[navigationId].definitionDetails = { ...data }
    },
    setDetails: (state, action: PayloadAction<{ navigationId: string; data: any }>) => {
      const { navigationId, data } = action.payload
      ensureNavigation(state, navigationId)
      state.navigationsById[navigationId].details = { ...data }
    },
    resetData: (state, action: PayloadAction<string | undefined>) => {
      if (action?.payload && state.navigationsById) {
        delete state.navigationsById[action.payload]
      } else {
        state.navigationsById = {}
      }
    },
  },
})

export const { setDefinitionDetails, setDetails, resetData } = siteNavigationSlice.actions
export default siteNavigationSlice

export const siteNavigationSelector = (state: any) => state.siteNavigation

export const makeDetailSelector = (navigationId: string) =>
  createSelector(
    [(state: any) => state.siteNavigation.navigationsById?.[navigationId]],
    (navState: ISiteNavigationInstanceState | undefined) => navState?.details ?? {},
  )

export const makeDefinitionDetailSelector = (navigationId: string) =>
  createSelector(
    [(state: any) => state.siteNavigation.navigationsById?.[navigationId]],
    (navState: ISiteNavigationInstanceState | undefined) => navState?.definitionDetails ?? {},
  )

// Legacy selectors for backward compatibility in shared components
export const detailSelector = createSelector([siteNavigationSelector], (siteNav) => {
  const ids = Object.keys(siteNav.navigationsById || {})
  const lastId = ids.at(-1)
  if (!lastId) return {}
  return siteNav.navigationsById[lastId]?.details ?? {}
})

export const definitionDetailSelector = createSelector([siteNavigationSelector], (siteNav) => {
  const ids = Object.keys(siteNav.navigationsById || {})
  const lastId = ids.at(-1)
  if (!lastId) return {}
  return siteNav.navigationsById[lastId]?.definitionDetails ?? {}
})

export const getDefinitionDetailById = createAsyncThunk(
  'siteNavigation/getDefinitionDetailById',
  async (payload: { navigationId: string; definitionId: string }, { dispatch }) => {
    dispatch(setLoader(true))
    const requestedData: SiteNavigationCRUDAPIDefinitionModal =
      WIDGET_MANAGEMENT_CRUD.GET_WIDGET_DEFINITION_BY_ID(payload.definitionId)
    const result: any = await mainApiService(requestedData)
    dispatch(setDefinitionDetails({ navigationId: payload.navigationId, data: result?.data }))
    dispatch(setLoader(false))
    return result
  },
)

export const getDetailById = createAsyncThunk(
  'siteNavigation/getDetailById',
  async (navigationId: string, { dispatch }) => {
    dispatch(setLoader(true))
    const requestedData: SiteNavigationCRUDAPIDefinitionModal =
      SITE_NAVIGATION_CRUD.GET_DETAIL_BY_ID(navigationId)
    const result: any = await mainApiService(requestedData)
    dispatch(setDetails({ navigationId, data: result?.data }))
    dispatch(setLoader(false))
    return result
  },
)
